import { FeatureCollection, Geometry } from 'geojson'
import L from 'leaflet'
import { useMemo } from 'react'
import { GeoJSON } from 'react-leaflet'

import { Map } from '@/components/map'

import { useCompareDiagnoses } from '../contexts/compare-diagnoses-context'
import FieldsNames from './components/fields-names'
import { NavigateToField } from './components/navigate-to-field'

interface WeedMapProps {
  children: React.ReactNode
  layerColors: Record<string, string>
  infestation?: FeatureCollection<Geometry, { layer: string }>
}

function InfestationMapComponent({
  infestation,
  layerColors,
  children,
}: WeedMapProps) {
  const { selectedFields, fieldColumnName } = useCompareDiagnoses()

  const { bounds, maxBounds } = useMemo(() => {
    if (!selectedFields.features.length) {
      const defaultBounds = L.latLngBounds(
        L.latLng(-15.0, -50.0),
        L.latLng(-12.0, -46.0),
      )
      return {
        bounds: defaultBounds,
        maxBounds: defaultBounds.pad(0.1),
      }
    }

    const bounds = L.geoJSON(selectedFields).getBounds()
    const maxBounds = bounds.pad(0.6)

    return { bounds, maxBounds }
  }, [selectedFields])

  return (
    <Map
      key={JSON.stringify({ bounds, maxBounds })}
      bounds={bounds}
      maxBounds={maxBounds}
      preferCanvas
      whenCreated={(map) =>
        maxBounds && map.setMinZoom(map.getBoundsZoom(maxBounds))
      }
      zoomSnap={1}
      markerZoomAnimation={false}
      disabledControls={{
        baseLayerControl: true,
        attributionControl: true,
      }}
      positionControls={{
        zoomControl: 'bottomright',
        centralizationControl: 'bottomright',
        scaleControl: 'bottomleft',
        attributionControl: 'bottomleft',
      }}
    >
      {children}

      <FieldsNames
        geojson={selectedFields}
        fieldColumnName={fieldColumnName!}
      />
      <GeoJSON
        data={selectedFields}
        style={{
          color: '#716FD0',
          weight: 2,
          fillRule: 'nonzero',
          fill: false,
        }}
      />
      {infestation && (
        <GeoJSON
          data={infestation}
          style={(feature) => ({
            color: layerColors[feature?.properties.layer],
            weight: 1,
            fill: false,
          })}
        />
      )}

      <NavigateToField />
    </Map>
  )
}

export const InfestationMap = InfestationMapComponent
